import React, { FC } from "react";
import { intervalToDuration } from "date-fns";
import {
  LifeUtilityAddOn,
  MAX_LIFE_LIMIT_CAT,
  MAX_LIFE_LIMIT_DOG,
  MIN_LIFE_LIMIT_CAT,
  MIN_LIFE_LIMIT_DOG,
} from "../../../../components-shared/insuranceData";
import LifeInsurance from "./LifeInsurance";
import { SpeciesEnum } from "../../../../../__generated__/globalTypes";
import { useWebTranslation } from "translations";
import { dateValid } from "utils/validation";
import { Typography } from "ui-kit/Typography";
import Gutter from "ui-kit/Gutter";
import Customiser from "../Customiser";
import { AddonBox } from "../AddonBox";
import frameCat from "../../../../../public/images/illustrations/frame-cat.svg";
import frameDog from "../../../../../public/images/illustrations/frame-dog.svg";

type ChooseLifeInsuranceProps = {
  showLifeInsurance: boolean;
  birthDay: string | null;
  startDate: string | null;
  lifeAgeGroup: number;
  setShowLifeInsurance: (shouldShow: boolean) => void;
  onChangeLifeLimit: (limit: number) => void;
  lifeLimit: number;
  animalName: string;
  species: SpeciesEnum;
  lifeUtilityAddOn: LifeUtilityAddOn;
  onChangeLifeUtilityAddOn: (utilityAddOn: LifeUtilityAddOn) => void;
};

const ChooseLifeInsurance: FC<ChooseLifeInsuranceProps> = ({
  showLifeInsurance,
  startDate,
  birthDay,
  lifeAgeGroup,
  lifeLimit,
  animalName,
  onChangeLifeLimit,
  species,
  setShowLifeInsurance,
  lifeUtilityAddOn,
  onChangeLifeUtilityAddOn,
}) => {
  const { t } = useWebTranslation();
  const isValidStartDate = dateValid(startDate ?? "");

  if (!birthDay) {
    return null;
  }

  if (isValidStartDate) {
    const years =
      intervalToDuration({
        start: new Date(birthDay),
        end: startDate ? new Date(startDate) : new Date(),
      }).years ?? 0;

    let tooOld = false;

    if (species === SpeciesEnum.cat && years >= 6) {
      tooOld = true;
    }

    if (
      species === SpeciesEnum.dog &&
      ((lifeAgeGroup === 8 && years >= 4) ||
        ((lifeAgeGroup === 10 || lifeAgeGroup === 12) && years >= 6))
    ) {
      tooOld = true;
    }

    if (tooOld) {
      return null;
    }
  }

  const description =
    species === SpeciesEnum.cat
      ? t("signup.step.addons.life_insurance.summary_cat")
      : t("signup.step.addons.life_insurance.summary_dog");

  const minLifeLimit =
    species === SpeciesEnum.dog ? MIN_LIFE_LIMIT_DOG : MIN_LIFE_LIMIT_CAT;
  const maxLifeLimit =
    species === SpeciesEnum.dog ? MAX_LIFE_LIMIT_DOG : MAX_LIFE_LIMIT_CAT;

  const utilityOptions = ["", "breeding", "hunting"];

  return (
    <>
      <AddonBox
        title={t("choose_life_insurance.option_title")}
        active={showLifeInsurance}
        summary={description}
        image={species === SpeciesEnum.cat ? frameCat : frameDog}
        switchDescription={t("choose_life_insurance.option_description")}
        readmore={
          species === SpeciesEnum.cat
            ? t("signup.step.addons.life_insurance.details_cat")
            : t("signup.step.addons.life_insurance.details_dog")
        }
        onChange={() => {
          if (showLifeInsurance) {
            onChangeLifeLimit(0);
            setShowLifeInsurance(false);
          } else {
            setShowLifeInsurance(true);
          }
        }}
        details={
          <>
            <LifeInsurance
              min={minLifeLimit}
              max={maxLifeLimit}
              title={t("choose_life_insurance.life_insurance.title", {
                name: animalName,
              })}
              value={lifeLimit}
              onChange={(limit) => onChangeLifeLimit(limit)}
            />
            <Typography variant="body2" color="grey500">
              {species === SpeciesEnum.cat
                ? t("signup.step.addons.life_insurance.details_cat")
                : t("signup.step.addons.life_insurance.details_dog")}
            </Typography>
            <Gutter size={3} />

            <Typography variant="body2">
              <strong>
                {t("choose_life_insurance.life_insurance.utility.title")}
              </strong>
            </Typography>
            <Gutter size={1} />
            <Typography color="grey500" variant="body2">
              {t(
                `choose_life_insurance.life_insurance.utility.description_${species}`
              )}
            </Typography>
            <Gutter size={2} />
            <Customiser
              options={[
                {
                  title: t(
                    "choose_life_insurance.life_insurance.utility.option.none"
                  ),
                  value: 0,
                },
                {
                  title: t(
                    "choose_life_insurance.life_insurance.utility.option.breeding"
                  ),
                  value: 1,
                },
                ...(species === "dog"
                  ? [
                      {
                        title: t(
                          "choose_life_insurance.life_insurance.utility.option.hunting"
                        ),
                        value: 2,
                      },
                    ]
                  : []),
              ]}
              value={utilityOptions.indexOf(lifeUtilityAddOn) ?? 0}
              onChange={(index) =>
                onChangeLifeUtilityAddOn(
                  utilityOptions[index] as LifeUtilityAddOn
                )
              }
              disabled={!lifeLimit}
            />
          </>
        }
      />
    </>
  );
};

export default ChooseLifeInsurance;
